/**
 * The scan ↔ hub transfer surface (card `t_8ae1d20d`/E2) — exported for reuse,
 * and used by the "3D Model" settings page (`views/Settings/SettingsModel.vue`).
 * E3 (the dashboard's scan entry) reuses the same verbs rather than growing a
 * second upload path.
 *
 * Four things happen here, and nothing else:
 *
 *   1. **Reading a capture off the device.** A capture is a directory in the app
 *      container (`Documents/RoomScan/<id>/`, written by the native `RoomScan`
 *      plugin). The files are read one by one through `@capacitor/filesystem`
 *      (`Directory.Documents`, a path relative to it) — lazily imported, so a
 *      browser/Node import of this module never needs the plugin — and rebuilt
 *      as `File`s, which is the shape the hub's multipart POST takes.
 *   2. **Checking a package before it travels** (`validatePickedPackage`). The
 *      hub refuses a bad name, a package over its limits and a `scan.json` that
 *      is not the contract; every one of those is decidable here, and a 100 MB
 *      refusal is a bad way to learn about a leading dot.
 *   3. **The three writes** — `POST /api/scans`, `PUT /api/scans/:id`, `DELETE
 *      /api/scans/:id` — with progress, and the local pairing (`captureId →
 *      scanId`) so the page knows what is already on the hub.
 *   4. **Getting a package back out**: the bytes come through the app's own
 *      transport, and where they land is the platform's business — a share
 *      sheet on iOS (Filesystem + the file-opener plugin), a download in a
 *      browser.
 *
 * Failures are thrown as `ScanTransferError`s carrying a **code**, not a
 * sentence: the surface that shows the message owns its language (the six
 * catalogues), and this module must not decide which of them the reader speaks.
 */
import { computed, ref } from 'vue';
import type {
  HubScan,
  ScanPackageAnswer,
  ScanPackageFields,
} from '../services/scanApi';
import {
  deleteHubScan as deleteHubScanRequest,
  fetchScanFile,
  replaceHubScan,
  scanPreviewFileName,
  scanPrimaryModelName,
  uploadScanPackage,
} from '../services/scanApi';
import type { RoomScanCapture, RoomScanCaptureSummary } from '../plugins/roomScan';
import { listRoomCaptures } from '../plugins/roomScan';
import { HubChannelUnavailableError } from '../services/hubRequestRouting';
import {
  captureDirectoryPath,
  capturePackageFileNames,
  scanContentType,
  scanDocContract,
  SCAN_DOC_NAME,
  validateScanPackage,
  type ScanPackageValidation,
} from '../utils/scanPackageFiles';
import { rememberScanUpload, forgetScanUpload } from '../utils/scanUploadRegistry';

/** Why a transfer did not finish — the surface translates the code. */
export type ScanTransferErrorCode =
  | 'no-hub'
  | 'auth'
  | 'validation'
  | 'read-failed'
  | 'upload-failed'
  | 'download-failed'
  | 'save-failed'
  | 'unknown';

/** A failed transfer, with the code a surface maps to its own sentence. */
export class ScanTransferError extends Error {
  readonly code: ScanTransferErrorCode;
  /** The cause's own words — shown as the second half of the sentence. */
  readonly detail: string;

  constructor(code: ScanTransferErrorCode, detail: string) {
    super(detail);
    this.name = 'ScanTransferError';
    this.code = code;
    this.detail = detail;
  }
}

function statusOf(error: unknown): number | undefined {
  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
}

function bodyOf(error: unknown): { error?: string; message?: string; details?: string[] } {
  const data = (error as { data?: unknown }).data;
  return data && typeof data === 'object' ? (data as { error?: string; message?: string }) : {};
}

/**
 * The one place a thrown hub error becomes a code — so every surface that
 * uploads reads "no hub connection" the same way the settings page does.
 */
export function scanTransferError(error: unknown): ScanTransferError {
  if (error instanceof ScanTransferError) return error;
  if (error instanceof HubChannelUnavailableError) {
    return new ScanTransferError('no-hub', error.message);
  }
  const status = statusOf(error);
  const body = bodyOf(error);
  const detail = body.error || body.message || (error instanceof Error ? error.message : String(error));
  if (status === 401 || status === 403) return new ScanTransferError('auth', detail);
  if (status === 400 || status === 413 || status === 415) return new ScanTransferError('validation', detail);
  return new ScanTransferError('upload-failed', detail);
}

/** `base64` → bytes; the shape `Filesystem.readFile` answers with on a device. */
function base64Bytes(base64: string, name: string): { bytes: Uint8Array<ArrayBuffer>; type: string } {
  const binary = atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return { bytes, type: scanContentType(name) };
}

/** The files of one capture, read off the device as `File`s the hub accepts. */
export async function readCaptureFiles(
  capture: Pick<RoomScanCaptureSummary, 'id' | 'files' | 'scanPath' | 'usdzPath' | 'jsonPath'>,
  onProgress?: (done: number, total: number) => void,
): Promise<File[]> {
  const names = capturePackageFileNames(capture);
  const files: File[] = [];
  try {
    const { Filesystem, Directory } = await import('@capacitor/filesystem');
    for (const name of names) {
      const result = await Filesystem.readFile({
        path: captureDirectoryPath(capture.id, name),
        directory: Directory.Documents,
      });
      const data = result.data;
      // Native answers base64 text; the web implementation answers a Blob.
      if (typeof data === 'string') {
        const { bytes, type } = base64Bytes(data, name);
        files.push(new File([bytes], name, { type }));
      } else {
        files.push(new File([data], name, { type: data.type || scanContentType(name) }));
      }
      onProgress?.(files.length, names.length);
    }
  } catch (error) {
    throw new ScanTransferError('read-failed', error instanceof Error ? error.message : String(error));
  }
  if (files.length === 0) {
    throw new ScanTransferError('read-failed', 'the capture directory listed no files');
  }
  return files;
}

/**
 * The whole verdict on a picked package, including the half a name cannot
 * answer: when the pick carries a `scan.json`, its text is read and checked
 * against the contract (`torik.scan/v0`) exactly as the hub will.
 */
export async function validatePickedPackage(files: File[]): Promise<ScanPackageValidation> {
  let scanDocContractValue: string | null = null;
  const doc = files.find((file) => file.name === SCAN_DOC_NAME);
  if (doc) {
    try {
      scanDocContractValue = scanDocContract(await doc.text());
    } catch {
      scanDocContractValue = null;
    }
  }
  return validateScanPackage(
    files.map((file) => ({ name: file.name, size: file.size })),
    { scanDocContract: doc ? scanDocContractValue : undefined },
  );
}

/** Upload a package; resolves with the stored scan (and the hub's own warnings). */
export async function uploadScanFiles(
  files: File[],
  fields: ScanPackageFields = {},
  onProgress?: (percent: number) => void,
): Promise<ScanPackageAnswer> {
  try {
    return await uploadScanPackage(files, fields, onProgress);
  } catch (error) {
    throw scanTransferError(error);
  }
}

/** Replace a stored scan's content with a locally picked package. */
export async function replaceScanFiles(
  scanId: string,
  files: File[],
  fields: ScanPackageFields = {},
  onProgress?: (percent: number) => void,
): Promise<ScanPackageAnswer> {
  try {
    return await replaceHubScan(scanId, files, fields, onProgress);
  } catch (error) {
    throw scanTransferError(error);
  }
}

/** The text fields a device capture contributes to its own row. */
export function capturePackageFields(
  capture: Pick<RoomScanCaptureSummary, 'id' | 'label' | 'mode' | 'roomCount'>,
  name?: string,
): ScanPackageFields {
  return {
    name: name?.trim() || capture.label || capture.id,
    label: capture.label || undefined,
    mode: capture.mode,
    roomCount: capture.roomCount,
    sourceId: capture.id,
  };
}

/**
 * Read a capture off this device and upload it — the path a finished capture
 * takes. The pairing is written only after the hub has answered, so a failed
 * upload leaves the capture offered again.
 */
export async function uploadCaptureToHub(
  capture: RoomScanCaptureSummary | RoomScanCapture,
  options: { name?: string; onProgress?: (percent: number) => void; onReading?: (done: number, total: number) => void } = {},
): Promise<HubScan> {
  const files = await readCaptureFiles(capture, options.onReading);
  const answer = await uploadScanFiles(files, capturePackageFields(capture, options.name), options.onProgress);
  rememberScanUpload({
    captureId: capture.id,
    scanId: answer.scan.id,
    name: answer.scan.name || capture.label || capture.id,
    uploadedAt: new Date().toISOString(),
  });
  return answer.scan;
}

/** Replace a stored scan with a capture from this device (E1's `PUT`). */
export async function replaceScanWithCapture(
  scanId: string,
  capture: RoomScanCaptureSummary,
  options: { name?: string; onProgress?: (percent: number) => void } = {},
): Promise<HubScan> {
  const files = await readCaptureFiles(capture);
  const answer = await replaceScanFiles(scanId, files, capturePackageFields(capture, options.name), options.onProgress);
  rememberScanUpload({
    captureId: capture.id,
    scanId: answer.scan.id,
    name: answer.scan.name || capture.label || capture.id,
    uploadedAt: new Date().toISOString(),
  });
  return answer.scan;
}

/** Delete a scan, and forget the local pairing that pointed at it. */
export async function deleteScanFromHub(scanId: string): Promise<void> {
  try {
    await deleteHubScanRequest(scanId);
  } catch (error) {
    throw scanTransferError(error);
  }
  forgetScanUpload(scanId);
}

/** A capture on this device that the hub does not have yet. */
export async function listDeviceCaptures(): Promise<RoomScanCaptureSummary[]> {
  try {
    return await listRoomCaptures();
  } catch {
    // A store that cannot be listed is not an error worth a red box: the page's
    // other half (the hub's scans) still works.
    return [];
  }
}

/** One file, on its way to the device. */
export interface ScanDownloadTarget {
  name: string;
  blob: Blob;
  contentType: string;
}

/** Fetch every file of a package, reporting each as it arrives. */
export async function fetchScanPackageFiles(
  scan: HubScan,
  onFile?: (file: ScanDownloadTarget) => void,
): Promise<ScanDownloadTarget[]> {
  const out: ScanDownloadTarget[] = [];
  try {
    for (const file of scan.files) {
      const { blob, contentType } = await fetchScanFile(scan.id, file.name, { download: true });
      const target = { name: file.name, blob, contentType };
      out.push(target);
      onFile?.(target);
    }
  } catch (error) {
    throw scanTransferError(error);
  }
  if (out.length === 0) throw new ScanTransferError('download-failed', 'the scan lists no files');
  return out;
}

/** A browser download: an object URL, one click, and the URL is released. */
function browserDownload(target: ScanDownloadTarget): void {
  const url = URL.createObjectURL(target.blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = target.name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // The click has already claimed the blob; releasing the URL now is safe and
  // keeps a long package from pinning every byte in memory.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Bytes → base64, for `Filesystem.writeFile` (which takes the data as text). */
async function blobBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  const CHUNK = 0x8000;
  for (let index = 0; index < bytes.length; index += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(index, index + CHUNK));
  }
  return btoa(binary);
}

/**
 * Save one file on the device: on iOS it is written into the app's cache and
 * handed to the system (the file-opener plugin's document interaction sheet —
 * "Save to Files", AirDrop, open in a 3D app); anywhere else it is a plain
 * browser download.
 */
export async function saveScanFileToDevice(target: ScanDownloadTarget): Promise<'shared' | 'downloaded'> {
  const platform = (await import('@capacitor/core')).Capacitor.getPlatform();
  if (platform !== 'ios') {
    browserDownload(target);
    return 'downloaded';
  }
  try {
    const { Filesystem, Directory } = await import('@capacitor/filesystem');
    const data = await blobBase64(target.blob);
    await Filesystem.writeFile({ path: target.name, directory: Directory.Cache, data, recursive: true });
    const { uri } = await Filesystem.getUri({ path: target.name, directory: Directory.Cache });
    const { FileOpener } = await import('@capacitor-community/file-opener');
    await FileOpener.open({ filePath: uri, contentType: target.contentType || 'application/octet-stream' });
    return 'shared';
  } catch (error) {
    throw new ScanTransferError('save-failed', error instanceof Error ? error.message : String(error));
  }
}

/**
 * Download a whole package: fetch every file, hand each one to the platform
 * (`saveScanFileToDevice`). Resolves with the number of files saved.
 */
export async function downloadScanPackage(
  scan: HubScan,
  onProgress?: (done: number, total: number) => void,
): Promise<number> {
  const files = await fetchScanPackageFiles(scan);
  let done = 0;
  for (const file of files) {
    await saveScanFileToDevice(file);
    done += 1;
    onProgress?.(done, files.length);
  }
  return files.length;
}

/** The blob a preview is drawn from — the plan/still the package carries, if any. */
export async function fetchScanPreview(scan: HubScan): Promise<string | null> {
  const name = scanPreviewFileName(scan);
  if (!name) return null;
  const { blob } = await fetchScanFile(scan.id, name);
  return URL.createObjectURL(blob);
}

/** What a model viewer would open from this package, if anything. */
export function scanModelName(scan: HubScan): string | null {
  return scanPrimaryModelName(scan);
}

/**
 * The reactive wrapper a surface uses: one transfer at a time, its phase, its
 * progress and its last failure. `progress` stays `null` until the transport
 * reports real bytes — a plain-HTTP hub has no upload progress, and a bar that
 * invents 0% is a lie about a 100 MB upload.
 */
export function useScanUpload() {
  const phase = ref<'idle' | 'reading' | 'uploading' | 'replacing' | 'downloading'>('idle');
  const progress = ref<number | null>(null);
  const error = ref<ScanTransferError | null>(null);
  const busy = computed(() => phase.value !== 'idle');

  function reset(): void {
    phase.value = 'idle';
    progress.value = null;
    error.value = null;
  }

  async function run<T>(next: (typeof phase)['value'], work: () => Promise<T>): Promise<T | null> {
    if (busy.value) return null;
    phase.value = next;
    progress.value = null;
    error.value = null;
    try {
      return await work();
    } catch (__caught: unknown) {
      error.value = scanTransferError(__caught);
      return null;
    } finally {
      phase.value = 'idle';
      progress.value = null;
    }
  }

  return {
    phase,
    progress,
    error,
    busy,
    reset,
    /** Read the device capture and upload it (progress from the transport). */
    async uploadCapture(capture: RoomScanCaptureSummary | RoomScanCapture, name?: string) {
      return await run('reading', async () => {
        const files = await readCaptureFiles(capture, () => {
          // The read is fast (a few MB off disk); percent stays unknown until
          // the upload itself starts reporting bytes.
        });
        phase.value = 'uploading';
        const answer = await uploadScanFiles(files, capturePackageFields(capture, name), (percent) => {
          progress.value = percent;
        });
        rememberScanUpload({
          captureId: capture.id,
          scanId: answer.scan.id,
          name: answer.scan.name || capture.label || capture.id,
          uploadedAt: new Date().toISOString(),
        });
        return answer.scan;
      });
    },
    /** Upload a package the user picked from the file system. */
    async uploadFiles(files: File[], fields: ScanPackageFields = {}) {
      return await run('uploading', async () => {
        const answer = await uploadScanFiles(files, fields, (percent) => {
          progress.value = percent;
        });
        if (fields.sourceId) {
          rememberScanUpload({
            captureId: fields.sourceId,
            scanId: answer.scan.id,
            name: answer.scan.name || fields.name || answer.scan.id,
            uploadedAt: new Date().toISOString(),
          });
        }
        return answer.scan;
      });
    },
    /** Swap a stored scan's content for a locally picked package. */
    async replaceWithFiles(scanId: string, files: File[], fields: ScanPackageFields = {}) {
      return await run('replacing', async () => {
        const answer = await replaceScanFiles(scanId, files, fields, (percent) => {
          progress.value = percent;
        });
        return answer.scan;
      });
    },
    /** Swap a stored scan's content for a capture on this device. */
    async replaceWithCapture(scanId: string, capture: RoomScanCaptureSummary) {
      return await run('replacing', async () => {
        phase.value = 'reading';
        const files = await readCaptureFiles(capture);
        phase.value = 'replacing';
        const answer = await replaceScanFiles(scanId, files, capturePackageFields(capture), (percent) => {
          progress.value = percent;
        });
        rememberScanUpload({
          captureId: capture.id,
          scanId: answer.scan.id,
          name: answer.scan.name || capture.label || capture.id,
          uploadedAt: new Date().toISOString(),
        });
        return answer.scan;
      });
    },
    /** Fetch every file of a package and hand it to the platform. */
    async download(scan: HubScan) {
      return await run('downloading', async () => await downloadScanPackage(scan));
    },
  };
}
